/**
 * Policy documents: v1 → v2 at read time (decision 0037, spec §3.2 and §2.14). Merged item capabilities keep the v1
 * split through the `level` constraint, constraints are renamed, and the 0032 backfill (close-as-done wherever
 * cancelling was) is a pure rule on the v1 document applied first. Nothing is written: the result depends on the
 * document alone, so the snapshot, PolicyCreated and PolicyRevised paths all agree.
 */
import type { Capability, Policy, PolicyDocument, PolicyRule, RuleConstraints } from "../domain/org"
import type * as V1 from "../legacy/v1/index"
import { fromV1Meta } from "./common"

type V1Capability = V1.Capability
type V1Rule = typeof V1.PolicyRule.Type
type V1Document = typeof V1.PolicyDocument.Type
type V1Constraints = typeof V1.RuleConstraints.Type

// ---- the 0032 backfill, verbatim from v1 core/policy.ts (protocol can't import core) ----

const v1Mentions = (doc: V1Document, cap: V1Capability): boolean =>
  doc.never.includes(cap) ||
  doc.allow.some((r) => r.capability === cap) ||
  doc.propose.some((r) => r.capability === cap)

/**
 * v1 `upgradedDocument` (decision 0032): `like`'s rules copied to `add` (constraints kept), or null when the document
 * doesn't mention `like` or already mentions `add` (a person's later choice is never overridden).
 */
export const v1UpgradedDocument = (
  doc: V1Document,
  add: V1Capability,
  like: V1Capability,
): V1Document | null => {
  if (!v1Mentions(doc, like) || v1Mentions(doc, add)) return null
  const copy = (rules: ReadonlyArray<V1Rule>): ReadonlyArray<V1Rule> =>
    rules.flatMap((r) =>
      r.capability === like ? [r, r.when ? { capability: add, when: r.when } : { capability: add }] : [r],
    )
  return {
    allow: copy(doc.allow),
    propose: copy(doc.propose),
    never: doc.never.includes(like) ? [...doc.never, add] : doc.never,
  }
}

/** The 0032 backfill as a read-time rule: a v1 document with cancel_task and no complete_task gains its twin. */
export const backfill0032 = (doc: V1Document): V1Document =>
  v1UpgradedDocument(doc, "complete_task", "cancel_task") ?? doc

// ---- capability mapping (§3.2) ----

type Level = "top" | "child"
type Target = { readonly capability: Capability; readonly level?: Level }

/** What one v1 capability becomes (rules get these, constraints kept and `level` added). */
export const V1_CAPABILITY_MAP: Readonly<Record<V1Capability, ReadonlyArray<Target>>> = {
  create_goal: [{ capability: "create_goal" }],
  update_goal: [{ capability: "update_goal" }],
  change_kpi_target: [{ capability: "change_kpi_target" }],
  define_kpi: [{ capability: "define_kpi" }],
  create_area: [{ capability: "create_area" }],
  update_area: [{ capability: "update_area" }],
  hire_agent: [{ capability: "hire_agent" }],
  update_agent: [{ capability: "update_agent" }],
  create_initiative: [{ capability: "create_item", level: "top" }],
  // v1 closed initiatives as done under update_initiative, and changed their owner with UpdateInitiative{ownerAgentId}
  update_initiative: [
    { capability: "update_item", level: "top" },
    { capability: "complete_item", level: "top" },
    { capability: "assign", level: "top" },
  ],
  start_initiative: [{ capability: "start_item" }],
  abandon_initiative: [{ capability: "cancel_item", level: "top" }],
  create_task: [{ capability: "create_item", level: "child" }],
  update_task: [{ capability: "update_item", level: "child" }],
  revise_contract: [{ capability: "revise_checks" }],
  cancel_task: [{ capability: "cancel_item", level: "child" }],
  complete_task: [{ capability: "complete_item", level: "child" }],
  reprioritize: [{ capability: "reprioritize" }],
  // v1 `assign` covered only AssignTask (every v1 task has a parent); without the level a v1 `propose assign` would
  // beat the `allow assign{top}` that update_initiative brings
  assign: [{ capability: "assign", level: "child" }],
  add_dependency: [{ capability: "add_dependency" }],
  remove_dependency: [{ capability: "remove_dependency" }],
  spawn_workers: [{ capability: "spawn_workers" }],
  propose_memory: [{ capability: "propose_memory" }],
  curate_memory: [{ capability: "curate_memory" }],
  outward_action: [{ capability: "outward_action" }],
  edit_policy: [{ capability: "edit_policy" }],
  grant_integration: [{ capability: "grant_integration" }],
}

/** The v2 names of a v1 capability (for `never`, `UpgradePolicies` and display). */
export const fromV1Capability = (c: V1Capability): ReadonlyArray<Capability> =>
  V1_CAPABILITY_MAP[c].map((t) => t.capability)

/** Constraints: own_initiative → own_tree, own_task → own_item, maxDepth d → d + 1 (depth below the top-level item). */
export const fromV1Constraints = (w: V1Constraints): RuleConstraints => {
  const out: {
    under?: RuleConstraints["under"]
    maxActive?: number
    maxConcurrent?: number
    to?: RuleConstraints["to"]
    ifWorkDone?: boolean
    maxDepth?: RuleConstraints["maxDepth"]
  } = {}
  if (w.under !== undefined)
    out.under = w.under === "own_initiative" ? "own_tree" : w.under === "own_task" ? "own_item" : w.under
  if (w.maxActive !== undefined) out.maxActive = w.maxActive
  if (w.maxConcurrent !== undefined) out.maxConcurrent = w.maxConcurrent
  if (w.to !== undefined) out.to = w.to
  if (w.ifWorkDone !== undefined) out.ifWorkDone = w.ifWorkDone
  if (w.maxDepth !== undefined) out.maxDepth = (w.maxDepth + 1) as RuleConstraints["maxDepth"]
  return out as RuleConstraints
}

const fromV1Rule = (r: V1Rule): ReadonlyArray<PolicyRule> =>
  V1_CAPABILITY_MAP[r.capability].map((t) => {
    const when: RuleConstraints = {
      ...(r.when ? fromV1Constraints(r.when) : {}),
      ...(t.level ? { level: t.level } : {}),
    }
    return Object.keys(when).length > 0 ? { capability: t.capability, when } : { capability: t.capability }
  })

const dedupe = <T>(xs: ReadonlyArray<T>): ReadonlyArray<T> => [...new Set(xs)]

/**
 * A v1 policy document as v2 (§3.2): the 0032 backfill first (on the v1 document), then every rule mapped. `never` maps
 * each capability to its merged names, so one half of a merged pair in `never` puts the merged capability in `never`
 * (tightening; a prohibition is never loosened).
 */
export const fromV1PolicyDocument = (doc: V1Document): PolicyDocument => {
  const d = backfill0032(doc)
  return {
    allow: d.allow.flatMap(fromV1Rule),
    propose: d.propose.flatMap(fromV1Rule),
    never: dedupe(d.never.flatMap(fromV1Capability)),
  }
}

export const fromV1Policy = (p: typeof V1.Policy.Type): Policy => ({
  ...fromV1Meta(p),
  document: fromV1PolicyDocument(p.document),
})
